'use client';

import { useRouter } from '@/hooks';
import { Box, Button, IconButton, Menu, MenuItem, Stack } from '@mui/material';
import { FC, MouseEvent } from 'react';
import { getFlagWithStyles } from '@/utils/locale/getFlag';
import { useAuthStore } from '@/store/authStore/authStore';
import { isArrayWithValues } from '@/utils/fp';
import { usePhrasesStore } from '@/store';
import { updateLocale } from '@/utils/locale/updateLocale';
import { getLocale, getSortedLocales } from '@/utils';
import { localeDict } from '@/consts/languageAccordance';
import { useThemeStore } from '@/store/themeStore';
import { useMenu } from '@/hooks';
import { useCurrentPath } from '@/hooks/general/useCurrentPath/useCurrentPath';
import { useLocale } from '@/hooks/general/useLocale/useLocale';
import { useIsLangSwitcherDisabled } from '@/hooks/helpers/useIsLangSwitcherDisabled/useIsLangSwitcherDisabled';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

interface LangSwitcherProps {
    customClass?: string;
    anchor?: HTMLElement | undefined;
    activeLinkLang?: string;
}

export const getCurrentLocaleParams = (i18nLocale: { locales: string[]; locale: string }) => {
    const currentLocale = getLocale(i18nLocale.locale);
    const currentCountry = currentLocale.country;

    const currentCountryName =
        currentCountry.name.en.toLowerCase() === 'united states of america'
            ? 'USA'
            : currentCountry.localeName;

    const currentLanguageName = currentLocale.language.localeName;

    return { currentLocale, currentCountry, currentCountryName, currentLanguageName };
};

export const LangSwitcher: FC<LangSwitcherProps> = ({ customClass, activeLinkLang }) => {
    const router = useRouter();
    const currentPath = useCurrentPath();
    const i18nLocale = useLocale();
    const { setLocale } = usePhrasesStore();
    const { variables } = useThemeStore();
    const { menuOpen, menuPosition, handleMenuClicked, handleMenuClose } = useMenu();
    const isLoggedIn = useAuthStore((s) => s.loggedIn);

    const isDisabled = useIsLangSwitcherDisabled();

    const handleMenuItemClick = async (evt: MouseEvent<HTMLLIElement>) => {
        await updateLocale({
            locale: evt.currentTarget.dataset.value,
            isLoggedIn,
            router,
            currentPath,
            i18nLocale,
            setLocale,
        });
        handleMenuClose();
    };

    const { currentLocale, currentCountry, currentCountryName, currentLanguageName } =
        getCurrentLocaleParams(i18nLocale);

    const sortedLocales = getSortedLocales(
        isLoggedIn,
        i18nLocale.locales,
        i18nLocale.locale,
        variables?.sortOrder?.locale || [],
    );

    return (
        <>
            {isArrayWithValues(sortedLocales) && (
                <>
                    {sortedLocales.length > 1 && !isDisabled ? (
                        <>
                            <Button
                                className={`language-button ${customClass ?? ''}`}
                                name="language-button"
                                data-testid="language-button"
                                variant="outlined"
                                id="language-button"
                                aria-controls={menuOpen ? 'language-menu' : undefined}
                                aria-expanded={menuOpen ? 'true' : undefined}
                                onClick={handleMenuClicked}
                            >
                                <FlagWithCountryName
                                    countryCode={currentCountry.code}
                                    countryName={currentCountryName}
                                    languageName={currentLanguageName}
                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mr: -1,
                                    }}
                                >
                                    {menuOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                                </Box>
                            </Button>
                            <Menu
                                id="language-menu"
                                data-testid="language-menu"
                                open={menuOpen}
                                anchorReference="anchorPosition"
                                anchorPosition={menuPosition}
                                slotProps={{
                                    paper: {
                                        className: 'lang-switcher-wrapper',
                                    },
                                }}
                                MenuListProps={{
                                    'aria-labelledby': 'language-button',
                                    className: 'lang-switcher',
                                }}
                                disableScrollLock
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                disableRestoreFocus
                            >
                                {sortedLocales.map((locale: keyof typeof localeDict) => (
                                    <MenuItem
                                        className="lang-switcher__item"
                                        selected={
                                            (router && locale === i18nLocale.locale) ||
                                            locale === activeLinkLang
                                        }
                                        onClick={handleMenuItemClick}
                                        key={`lang-menu-item-${locale}`}
                                        data-value={locale}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={0.25}
                                            sx={{
                                                alignItems: 'center',
                                            }}
                                        >
                                            {getFlagWithStyles(getLocale(locale).countryCode)}
                                            <span data-value={currentLocale}>{`${
                                                getLocale(locale).country.localeName
                                            } (${getLocale(locale).language.localeName})`}</span>
                                        </Stack>
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    ) : (
                        <IconButton
                            aria-label="Change locale"
                            className={`language-info ${customClass ?? ''}`}
                            disableFocusRipple
                            disableRipple
                            data-testid="language-info"
                        >
                            <FlagWithCountryName
                                countryCode={currentCountry.code}
                                countryName={currentCountryName}
                                languageName={currentLanguageName}
                            />
                        </IconButton>
                    )}
                </>
            )}
        </>
    );
};

interface FlagWithCountryNameProps {
    countryCode: string;
    countryName: string;
    languageName: string;
}

export const FlagWithCountryName: FC<FlagWithCountryNameProps> = ({
    countryCode,
    countryName,
    languageName,
}) => (
    <Stack
        direction="row"
        sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            mr: { xs: -1, lg: 0 },
        }}
    >
        {getFlagWithStyles(countryCode)}
        <Box
            className="SelectedLocale"
            sx={{
                display: { xs: 'none', lg: 'inline-flex' },
                ml: { xs: 0, lg: 0.25 },
            }}
        >
            {countryName} ({languageName})
        </Box>
    </Stack>
);
